import React from "react"
import {
    Heading,
    Text,
    Divider,
    Link,
    UnorderedList,
    OrderedList,
    ListItem,
    Image,
    AspectRatio,
} from "@chakra-ui/react"
import { BLOCKS, MARKS, INLINES } from "@contentful/rich-text-types"
import {
    renderRichText,
    RenderRichTextData,
    ContentfulRichTextGatsbyReference,
} from "gatsby-source-contentful/rich-text"
import ReactPlayer from "react-player"

export function useRichTextContent(
    data?: RenderRichTextData<ContentfulRichTextGatsbyReference> | null
) {
    if (!data) return <></>

    return renderRichText(data, {
        renderMark: {
            [MARKS.BOLD]: text => (
                <Text as="span" fontWeight={700}>
                    {text}
                </Text>
            ),
            [MARKS.ITALIC]: text => (
                <Text as="span" fontStyle="italic">
                    {text}
                </Text>
            ),
            [MARKS.UNDERLINE]: text => (
                <Text as="span" textDecoration="underline">
                    {text}
                </Text>
            ),
            [MARKS.CODE]: text => (
                <Text as="span" fontFamily="mono">
                    {text}
                </Text>
            ),
        },
        renderNode: {
            [BLOCKS.HEADING_1]: (node, children) => (
                <Heading
                    mt={12}
                    mb={2}
                    as="h1"
                    size="xl"
                    id={getValidId(
                        node.content[0].nodeType === "text"
                            ? node.content[0].value
                            : ""
                    )}
                >
                    {children}
                </Heading>
            ),
            [BLOCKS.HEADING_2]: (node, children) => (
                <Heading
                    mt={12}
                    mb={2}
                    as="h2"
                    size="lg"
                    fontWeight={"600"}
                    id={getValidId(
                        node.content[0].nodeType === "text"
                            ? node.content[0].value
                            : ""
                    )}
                >
                    {children}
                </Heading>
            ),
            [BLOCKS.HEADING_3]: (node, children) => (
                <Heading
                    mt={12}
                    mb={2}
                    as="h3"
                    size="lg"
                    fontWeight={"600"}
                    id={getValidId(
                        node.content[0].nodeType === "text"
                            ? node.content[0].value
                            : ""
                    )}
                >
                    {children}
                </Heading>
            ),
            [BLOCKS.HEADING_4]: (node, children) => (
                <Heading
                    mt={12}
                    mb={2}
                    as="h4"
                    size="lg"
                    fontWeight={"600"}
                    id={getValidId(
                        node.content[0].nodeType === "text"
                            ? node.content[0].value
                            : ""
                    )}
                >
                    {children}
                </Heading>
            ),
            [BLOCKS.HEADING_5]: (node, children) => (
                <Heading
                    mt={12}
                    mb={2}
                    as="h5"
                    size="md"
                    fontWeight={"600"}
                    id={getValidId(
                        node.content[0].nodeType === "text"
                            ? node.content[0].value
                            : ""
                    )}
                >
                    {children}
                </Heading>
            ),
            [BLOCKS.HEADING_6]: (node, children) => (
                <Heading
                    mt={12}
                    mb={2}
                    as="h6"
                    size="sm"
                    fontWeight={"600"}
                    id={getValidId(
                        node.content[0].nodeType === "text"
                            ? node.content[0].value
                            : ""
                    )}
                >
                    {children}
                </Heading>
            ),
            [BLOCKS.PARAGRAPH]: (_node, children) => (
                <Text
                    letterSpacing={"wide"}
                    lineHeight={"taller"}
                    mb={6}
                    fontSize="18px"
                >
                    {children}
                </Text>
            ),
            [BLOCKS.UL_LIST]: (_node, children) => (
                <UnorderedList ml={4} mb={4}>
                    {children}
                </UnorderedList>
            ),
            [BLOCKS.OL_LIST]: (_node, children) => (
                <OrderedList ml={4} mb={4}>
                    {children}
                </OrderedList>
            ),
            [BLOCKS.LIST_ITEM]: (_node, children) => (
                <ListItem ml={2} mt={-4}>
                    {children}
                </ListItem>
            ),
            [BLOCKS.HR]: () => <Divider my={3} />,
            [INLINES.HYPERLINK]: (node, children) => {
                const url = node.data.uri as string

                if (url.includes("youtube.com"))
                    return (
                        <AspectRatio
                            marginTop={8}
                            marginBottom={8}
                            ratio={16 / 9}
                        >
                            <ReactPlayer
                                url={url}
                                width="100%"
                                height="100%"
                                controls={true}
                            />
                        </AspectRatio>
                    )

                return (
                    <Link href={url}>
                        <Text
                            as="span"
                            fontWeight="bold"
                            textDecor="underline"
                            _hover={{ color: "blue.600" }}
                            color="blue.500"
                        >
                            {children}
                        </Text>
                    </Link>
                )
            },
            [BLOCKS.EMBEDDED_ASSET]: node => {
                if (!node.data?.target?.file) return <></>

                const { title, description, file } = node.data?.target
                const mimeType = file.contentType
                const mimeGroup = mimeType.split("/")[0]
                const url = file.url
                switch (mimeGroup) {
                    case "image":
                        return (
                            <Image
                                src={url}
                                alt={description ? description : title}
                            />
                        )
                    default:
                        return (
                            <span style={{ backgroundColor: "red" }}>
                                {mimeType} embedded asset{" "}
                            </span>
                        )
                }
            },
            [BLOCKS.EMBEDDED_ENTRY]: node => {
                if (!node?.data?.target?.url) return <></>

                return (
                    <AspectRatio marginTop={8} marginBottom={8} ratio={16 / 9}>
                        <ReactPlayer
                            url={node.data.target.url}
                            width="100%"
                            height="100%"
                            controls={true}
                        />
                    </AspectRatio>
                )
            },
        },
    })
}

export function getValidId(text: string) {
    // Turns text into a valid ID to be used as an anchor
    const result = (
        "topic-" +
        text
            .toLowerCase()
            .replace(/ /g, "-")
            .replace(/[^\w-]+/g, "")
    ).replace(/--+/g, "-")
    return result
}
